import {type FormEvent, useMemo, useState} from "react";
import {useNavigate} from "react-router-dom";

import {useMutation, useQuery} from "@tanstack/react-query";
import {ChevronDownIcon, Loader2Icon} from "lucide-react";

import {type OsImage, type Plan, osImagesList, plansList, serversCreate} from "@/sdk";

import {Button} from "@/components/ui/button";
import {Card, CardContent, CardDescription, CardHeader, CardTitle} from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {Input} from "@/components/ui/input";
import {Label} from "@/components/ui/label";
import {Textarea} from "@/components/ui/textarea";

import {formatGB, formatMB, formatPrice} from "@/lib/format";

export const ServerNew = () => {
  const navigate = useNavigate();
  const [hostname, setHostname] = useState("");
  const [label, setLabel] = useState("");
  const [planId, setPlanId] = useState<string | null>(null);
  const [osImageId, setOsImageId] = useState<string | null>(null);
  const [sshKey, setSshKey] = useState("");
  const [error, setError] = useState<string | null>(null);

  const plansQ = useQuery({
    queryKey: ["plans", "list"],
    queryFn: async () => {
      const {data, error: e} = await plansList();
      if (e) throw e;
      return data?.items ?? [];
    },
  });

  const imagesQ = useQuery({
    queryKey: ["os-images", "list"],
    queryFn: async () => {
      const {data, error: e} = await osImagesList();
      if (e) throw e;
      return data?.items ?? [];
    },
  });

  const selectedPlan = useMemo<Plan | undefined>(
    () => plansQ.data?.find((p) => p.id === planId),
    [plansQ.data, planId],
  );
  const selectedImage = useMemo<OsImage | undefined>(
    () => imagesQ.data?.find((i) => i.id === osImageId),
    [imagesQ.data, osImageId],
  );

  const createM = useMutation({
    mutationFn: () =>
      serversCreate({
        body: {
          hostname,
          label: label || undefined,
          plan_id: planId!,
          os_image_id: osImageId!,
          ssh_public_key: sshKey || undefined,
        },
      }),
    onSuccess: ({data, error: e}) => {
      if (e || !data) {
        const detail = (e as {detail?: string} | undefined)?.detail ?? "Create failed";
        setError(detail);
        return;
      }
      navigate(`/servers/${data.id}`, {replace: true});
    },
    onError: (e) => setError(e instanceof Error ? e.message : "Create failed"),
  });

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!planId || !osImageId) {
      setError("Pick a plan and an OS image.");
      return;
    }
    createM.mutate();
  };

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <div>
        <h1 className="font-heading text-2xl font-semibold">Create server</h1>
        <p className="text-sm text-muted-foreground">
          Provision a new VM in your organization.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Basics</CardTitle>
          <CardDescription>Identifying details for the new VM.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-5" noValidate>
            <div className="space-y-2">
              <Label htmlFor="hostname">Hostname</Label>
              <Input
                id="hostname"
                placeholder="web01.example.com"
                required
                value={hostname}
                onChange={(e) => setHostname(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="label">Label (optional)</Label>
              <Input
                id="label"
                placeholder="Production web server"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label>Plan</Label>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full justify-between"
                    disabled={plansQ.isLoading}
                  >
                    <span className="truncate text-left">
                      {plansQ.isLoading ? (
                        <Loader2Icon className="size-4 animate-spin" />
                      ) : selectedPlan ? (
                        <>
                          {selectedPlan.name} · {selectedPlan.vcpus} vCPU ·{" "}
                          {formatMB(selectedPlan.memory_mb)} ·{" "}
                          {formatGB(selectedPlan.disk_gb)}
                        </>
                      ) : (
                        <span className="text-muted-foreground">Select a plan…</span>
                      )}
                    </span>
                    <ChevronDownIcon className="size-4 opacity-50" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-[var(--radix-dropdown-menu-trigger-width)]">
                  {(plansQ.data ?? []).length === 0 ? (
                    <DropdownMenuItem disabled>No plans available</DropdownMenuItem>
                  ) : (
                    plansQ.data!.map((p) => (
                      <DropdownMenuItem key={p.id} onSelect={() => setPlanId(p.id)}>
                        <div className="flex w-full flex-col">
                          <span className="font-medium">{p.name}</span>
                          <span className="text-xs text-muted-foreground">
                            {p.vcpus} vCPU · {formatMB(p.memory_mb)} ·{" "}
                            {formatGB(p.disk_gb)} ·{" "}
                            {formatPrice(p.price_cents, p.currency)}
                          </span>
                        </div>
                      </DropdownMenuItem>
                    ))
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            <div className="space-y-2">
              <Label>OS image</Label>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full justify-between"
                    disabled={imagesQ.isLoading}
                  >
                    <span className="truncate text-left">
                      {imagesQ.isLoading ? (
                        <Loader2Icon className="size-4 animate-spin" />
                      ) : selectedImage ? (
                        <>
                          {selectedImage.name} · {selectedImage.arch}
                        </>
                      ) : (
                        <span className="text-muted-foreground">Select an OS image…</span>
                      )}
                    </span>
                    <ChevronDownIcon className="size-4 opacity-50" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-[var(--radix-dropdown-menu-trigger-width)]">
                  {(imagesQ.data ?? []).length === 0 ? (
                    <DropdownMenuItem disabled>No OS images available</DropdownMenuItem>
                  ) : (
                    imagesQ.data!.map((img) => (
                      <DropdownMenuItem key={img.id} onSelect={() => setOsImageId(img.id)}>
                        <div className="flex w-full flex-col">
                          <span className="font-medium">{img.name}</span>
                          <span className="text-xs text-muted-foreground">
                            {img.family} {img.version} · {img.arch}
                          </span>
                        </div>
                      </DropdownMenuItem>
                    ))
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            <div className="space-y-2">
              <Label htmlFor="ssh">SSH public key (optional)</Label>
              <Textarea
                id="ssh"
                placeholder="ssh-ed25519 AAAA…"
                rows={4}
                value={sshKey}
                onChange={(e) => setSshKey(e.target.value)}
              />
            </div>

            {error ? (
              <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {error}
              </p>
            ) : null}

            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => navigate(-1)}>
                Cancel
              </Button>
              <Button type="submit" disabled={createM.isPending}>
                {createM.isPending ? "Creating…" : "Create server"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};
